/**
 * Resources API wrapper.
 *
 * **v2 resource entity (DNKADM-0054, handoff §5.7).** Resources ("bays") are now a
 * FIRST-CLASS `v2_resource` entity with **capacity** — replacing the v1 Any-Bay /
 * ServiceAccount machinery entirely (§9.1/§9.2). This module owns the v2 entity CRUD +
 * soft-delete/reactivate (§4.3), per-(resource, location) weekly schedules, date
 * exceptions, location assignments, and the fail-closed `getResourceOptions()` fetcher
 * `ServiceDetail` passes through `usePickList`. Bearer-only `/v2/admin/**` surface —
 * `request()` routes it (DNKADM-0049 `isV2Surface`): bearer token, NO CSRF, NO cookies,
 * the bounded 401→refresh→retry; the mutating-audit `X-Session-Mode` stamp fires on the writes
 * (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.7, §3.1 — standard REST + soft-delete):**
 *  - `GET    /v2/admin/resources?status=active|inactive|all` → {@link V2Resource}`[]`.
 *  - `GET    /v2/admin/resources/{id}` → one {@link V2Resource}.
 *  - `POST   /v2/admin/resources` ← {@link ResourceInput} → the created row.
 *  - `PUT    /v2/admin/resources/{id}` ← {@link ResourceInput} → the updated row.
 *  - `POST   /v2/admin/resources/{id}/deactivate` | `/reactivate` (§4.3).
 *  - `GET/PUT /v2/admin/resources/{id}/location-assignments` ← `{ locationIds: [...] }`.
 *  - `GET/PUT /v2/admin/resources/{id}/schedule?locationId=…` ← `{ days: [...] }` (per
 *    location — a resource at two locations has two schedules; times are the
 *    location's local time).
 *  - `GET/POST/DELETE /v2/admin/resources/{id}/date-exceptions`.
 *
 * **`v2_resource` field set (handoff §5.7):** `name`, `description` (opt),
 * **`capacity`** (INT ≥ 1, default 1 — max simultaneous reservations; >1 means N
 * concurrent bookings, availability returns `resourceCapacityRemaining`), `isActive`.
 * Unique on `(tenant, name)` — the create/update `409` surfaces inline.
 *
 * **`dayOfWeek` is 0=Sun … 6=Sat** here (the §5.6/§5.7 staff/resource convention; the
 * editor renders Sun→Sat). Shift validation (`@/utils/shiftValidation`, the SHARED
 * DNKADM-0053 util) mirrors the server: `start < end`, no overlaps/duplicates within a
 * day/date/scope, overnight disallowed.
 *
 * **Sub-resource wire shapes are ASSUMED standard REST** (handoff §10 — the live
 * OpenAPI "v2" group is authoritative when available; recorded in the DNKADM-0054
 * delivery's Notes). Every read is parsed DEFENSIVELY (mirrors `locations.ts` /
 * `staff.ts`): a missing/wrong-typed field degrades (an entry with no usable `id`/key
 * is dropped), never a throw.
 *
 * The Reservations Calendar also derives its bay lanes from {@link listResources};
 * there is no separate legacy roster read.
 */
import { request } from './client';
import { PickListError } from './picklists';
import { parseRowLocations, type LocationSummary } from './locations';
import type { ActiveFilterValue } from '@/hooks/useActiveFilter';
import type { ShiftLike } from '@/utils/shiftValidation';

/** v2 admin resources base path (bearer surface). */
const PATH = '/v2/admin/resources';

/** The seven days of the week, `dayOfWeek` 0=Sunday … 6=Saturday (§5.7 convention). */
const RESOURCE_DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6] as const;

/**
 * One `v2_resource` row (§5.7). `description` is an optional string (absent when the
 * server carries none — `exactOptionalPropertyTypes`: the key is omitted, never
 * `undefined`). `capacity` is the max simultaneous reservations (INT ≥ 1; >1 ⇒ N
 * concurrent bookings — availability returns `resourceCapacityRemaining`, not computed
 * here, just stored).
 */
export interface V2Resource {
  id: string;
  name: string;
  /** Max simultaneous reservations (INT ≥ 1; default 1). */
  capacity: number;
  /** Soft-delete flag (§4.3); `false` = deactivated. */
  isActive: boolean;
  description?: string;
  /**
   * The owned location(s) this resource is assigned to (DNKADM-0078), carried
   * INLINE on the list row (`HANDOFF-dink-api-list-row-locations`) so the list
   * screen renders an assigned-Locations column with NO per-row fan-out. Absent
   * (`undefined`) = unknown / field not emitted yet (the column shows "—"); an
   * explicit `[]` = assigned to no locations (the column shows red "No locations").
   */
  locations?: LocationSummary[];
}

/**
 * Create/update body for a resource. `name` + `capacity` are required by the form;
 * `description` is optional. `isActive` is NOT settable here — activation is the
 * dedicated deactivate/reactivate endpoints (§4.3).
 */
export interface ResourceInput {
  name: string;
  capacity: number;
  description?: string;
}

/** Options for {@link listResources}. */
export interface ListResourcesOptions {
  /** Active/Inactive/All filter (§4.3); defaults to `active` (live rows only). */
  status?: ActiveFilterValue;
  /**
   * Convenience flag (mirrors the spec's `listResources({ includeInactive? })`):
   * `true` ⇒ `status=all`. Ignored when an explicit `status` is also passed.
   */
  includeInactive?: boolean;
}

/** The minimal `{ id, name }` option {@link getResourceOptions} returns. */
export interface ResourceOption {
  id: string;
  name: string;
  isActive: boolean;
}

/** One shift within a schedule day / exception (`"HH:mm"`, local). */
export type ResourceShift = ShiftLike;

/**
 * One day of a per-(resource, location) weekly schedule. `dayOfWeek` 0=Sun … 6=Sat.
 * When `isAvailable` is false the day is off and carries no shifts.
 */
export interface ResourceScheduleDay {
  dayOfWeek: number;
  isAvailable: boolean;
  shifts: ResourceShift[];
}

/** A parsed `GET …/schedule` — the seven days in `dayOfWeek` 0→6 order. */
export interface ResourceSchedule {
  locationId: string | null;
  days: ResourceScheduleDay[];
}

/** A date exception's scope (§5.7): all locations, or one location. */
export type ExceptionScope = 'ALL' | 'LOCATION';

/**
 * One resource date exception (§5.7). `exceptionDate` is a local `YYYY-MM-DD`;
 * `isAvailable` false = off that date. `scope` is `ALL` (every location) or
 * `LOCATION` (one location, `locationId` set). When available, optional override
 * `shifts`. `id` is the server row id (present on reads; used to DELETE).
 */
export interface ResourceDateException {
  id: string | null;
  exceptionDate: string;
  isAvailable: boolean;
  scope: ExceptionScope;
  /** The scoped location id when `scope === 'LOCATION'`; null/absent for `ALL`. */
  locationId: string | null;
  shifts: ResourceShift[];
}

/** Create body for a date exception (no `id` — the server assigns it). */
export interface ResourceDateExceptionInput {
  exceptionDate: string;
  isAvailable: boolean;
  scope: ExceptionScope;
  /** Required when `scope === 'LOCATION'`; omitted for `ALL`. */
  locationId?: string;
  shifts: ResourceShift[];
}

// --- Defensive parsing helpers (mirrors locations.ts / staff.ts) ---------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity ids arrive
 * as numbers on the wire and are normalized to the `string` the UI/router use — a
 * finite number → `String(n)`, a non-empty string → trimmed, else null. Preserves
 * genuinely-string ids (accepts both); a numeric id is never dropped.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A boolean from a real boolean, or a fallback when absent/unparseable. */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** A `"HH:mm"` time string (24h), or null. */
function asTime(value: unknown): string | null {
  const text = asText(value);
  if (text === null) return null;
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(text) ? text : null;
}

/** A `YYYY-MM-DD` date string, or null. */
function asDate(value: unknown): string | null {
  const text = asText(value);
  if (text === null) return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
}

/** A `dayOfWeek` index in 0–6 (number or numeric string), or null. */
function asDayOfWeek(value: unknown): number | null {
  let n: number | null = null;
  if (typeof value === 'number' && Number.isInteger(value)) n = value;
  else if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isInteger(parsed)) n = parsed;
  }
  if (n === null || n < 0 || n > 6) return null;
  return n;
}

/**
 * A positive integer capacity (number or numeric string), or null. `capacity` is INT
 * ≥ 1 (§5.7); a missing/zero/negative/fractional value is not usable, so on read it
 * degrades to the default 1 (see {@link parseResource}).
 */
function asCapacity(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 1) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isInteger(parsed) && parsed >= 1) return parsed;
  }
  return null;
}

/**
 * Parse one raw resource row, or null when it has no usable `id` (a half-identified
 * entry is dropped rather than surfaced as a selectable option). A missing `name`
 * degrades to an empty string (the row still renders; the server is the authority). A
 * missing/invalid `capacity` degrades to the default 1 (§5.7). `description` is
 * assigned only when present (exactOptionalPropertyTypes).
 */
function parseResource(raw: unknown): V2Resource | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  if (id === null) return null;

  const resource: V2Resource = {
    id,
    name: asText(raw['name']) ?? '',
    capacity: asCapacity(raw['capacity']) ?? 1,
    // The wire flag is `active` (DNKADM-0064); tolerate legacy `isActive`.
    isActive: asBoolean(raw['active'] ?? raw['isActive'], true),
  };
  const description = asText(raw['description']);
  if (description !== null) resource.description = description;
  // Inline assigned-locations summary (DNKADM-0078) — assigned only when the field
  // is present (absent ⇒ key omitted, exactOptionalPropertyTypes; absent ≠ empty).
  const locations = parseRowLocations(raw);
  if (locations !== undefined) resource.locations = locations;
  return resource;
}

/**
 * Extract the resources array TOLERANTLY from a successful body, returning `null` when
 * NEITHER a bare array NOR a known wrapper key (`resources`/`content`) yields one. The
 * list read turns `null` into `[]` (a real first-run state, §8); the fail-closed
 * pick-list turns it into a {@link PickListError} (a non-array success cannot be
 * trusted as "complete and empty").
 */
function extractResources(body: unknown): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['resources'])) return body['resources'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return null;
}

/** Build the JSON body for a create/update, omitting an absent `description`. */
function toResourceBody(input: ResourceInput): Record<string, unknown> {
  const json: Record<string, unknown> = { name: input.name, capacity: input.capacity };
  if (input.description !== undefined) json['description'] = input.description;
  return json;
}

// --- Defensive parsing: schedule / exceptions ----------------------------------

/** Parse a raw shifts array, dropping any entry without a valid `"HH:mm"` start AND end. */
function parseShifts(raw: unknown): ResourceShift[] {
  if (!Array.isArray(raw)) return [];
  const shifts: ResourceShift[] = [];
  for (const entry of raw) {
    if (!isObject(entry)) continue;
    const startTime = asTime(entry['startTime']);
    const endTime = asTime(entry['endTime']);
    if (startTime !== null && endTime !== null) shifts.push({ startTime, endTime });
  }
  return shifts;
}

/**
 * Parse one raw schedule day keyed by its `dayOfWeek`, or null without a usable
 * `dayOfWeek`. A day is unavailable when explicitly closed OR it carries no valid
 * shifts; an unavailable day carries no shifts.
 */
function parseScheduleDay(raw: unknown): { dayOfWeek: number; day: ResourceScheduleDay } | null {
  if (!isObject(raw)) return null;
  const dayOfWeek = asDayOfWeek(raw['dayOfWeek']);
  if (dayOfWeek === null) return null;
  const shifts = parseShifts(raw['shifts']);
  const isAvailable = asBoolean(raw['isAvailable'], false) && shifts.length > 0;
  return {
    dayOfWeek,
    day: isAvailable
      ? { dayOfWeek, isAvailable: true, shifts }
      : { dayOfWeek, isAvailable: false, shifts: [] },
  };
}

/** An unavailable-by-default day for a `dayOfWeek` the server didn't return. */
function defaultUnavailableDay(dayOfWeek: number): ResourceScheduleDay {
  return { dayOfWeek, isAvailable: false, shifts: [] };
}

/** Normalize a raw `days` payload to exactly the seven days in `dayOfWeek` 0→6 order. */
function parseScheduleDays(raw: unknown): ResourceScheduleDay[] {
  const byDay = new Map<number, ResourceScheduleDay>();
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      const parsed = parseScheduleDay(entry);
      if (parsed !== null) byDay.set(parsed.dayOfWeek, parsed.day);
    }
  }
  return RESOURCE_DAYS_OF_WEEK.map((d) => byDay.get(d) ?? defaultUnavailableDay(d));
}

function parseSchedule(raw: unknown): ResourceSchedule {
  const o = isObject(raw) ? raw : {};
  return { locationId: asId(o['locationId']), days: parseScheduleDays(o['days']) };
}

/** Parse a raw exception scope; anything but `LOCATION` (case-insensitively) is `ALL`. */
function parseScope(value: unknown): ExceptionScope {
  return typeof value === 'string' && value.toUpperCase() === 'LOCATION' ? 'LOCATION' : 'ALL';
}

/** Parse one raw date exception, or null without a usable `exceptionDate`. */
function parseException(raw: unknown): ResourceDateException | null {
  if (!isObject(raw)) return null;
  const exceptionDate = asDate(raw['exceptionDate']);
  if (exceptionDate === null) return null;
  // DNKADM-0388 U1: the response key is `available` (dink-api's DateExceptionResponse);
  // `isAvailable` is read only as a fallback when `available` is absent.
  const isAvailable = asBoolean(raw['available'] ?? raw['isAvailable'], false);
  const scope = parseScope(raw['scope']);
  return {
    id: asId(raw['id']),
    exceptionDate,
    isAvailable,
    scope,
    locationId: scope === 'LOCATION' ? asId(raw['locationId']) : null,
    shifts: isAvailable ? parseShifts(raw['shifts']) : [],
  };
}

/** Tolerantly extract a list array (bare array or `{ <key> }`/`{ content }`). */
function extractList(body: unknown, key: string): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body[key])) return body[key] as unknown[];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return [];
}

// --- Serialization helpers -----------------------------------------------------

function shiftsToBody(shifts: ResourceShift[]): Array<{ startTime: string; endTime: string }> {
  return shifts.map((s) => ({ startTime: s.startTime, endTime: s.endTime }));
}

function scheduleDayToBody(day: ResourceScheduleDay): Record<string, unknown> {
  return day.isAvailable
    ? { dayOfWeek: day.dayOfWeek, isAvailable: true, shifts: shiftsToBody(day.shifts) }
    : { dayOfWeek: day.dayOfWeek, isAvailable: false, shifts: [] };
}

function exceptionToBody(ex: ResourceDateExceptionInput): Record<string, unknown> {
  // DNKADM-0388 U1: the wire key is `available` — dink-api's date-exception request
  // record (`v2/resource/DateExceptionRequest.available`) has NO `@JsonProperty`, unlike the
  // WEEKLY `ScheduleDayDto`, which is pinned to `isAvailable` (DNKAPI-0162). Sending
  // `isAvailable` here was silently ignored and every exception saved as closed.
  const json: Record<string, unknown> = {
    exceptionDate: ex.exceptionDate,
    available: ex.isAvailable,
    scope: ex.scope,
    shifts: ex.isAvailable ? shiftsToBody(ex.shifts) : [],
  };
  if (ex.scope === 'LOCATION' && ex.locationId !== undefined) {
    json['locationId'] = ex.locationId;
  }
  return json;
}

// --- Path helpers --------------------------------------------------------------

function resourcePath(id: string): string {
  return `${PATH}/${encodeURIComponent(id)}`;
}

// --- Entity endpoints (DNKADM-0054 U1) -----------------------------------------

/**
 * `GET /v2/admin/resources?status=…` → the tenant's resources, defensively parsed (a
 * bad entry dropped). The `status` filter (§4.3) defaults to `active` (live rows
 * only); pass `status: 'inactive'|'all'` or `includeInactive: true` to include
 * deactivated rows. Throws a typed `ApiError` on a non-2xx response.
 */
export async function listResources(options: ListResourcesOptions = {}): Promise<V2Resource[]> {
  const status = options.status ?? (options.includeInactive ? 'all' : 'active');
  const body = await request<unknown>('GET', PATH, { query: { status } });
  return (extractResources(body) ?? [])
    .map(parseResource)
    .filter((r): r is V2Resource => r !== null);
}

/**
 * `GET /v2/admin/resources/{id}` → one resource (defensively parsed). Throws a typed
 * `ApiError` (`404` NOT_FOUND, upstream forwarded).
 */
export async function getResource(id: string): Promise<V2Resource | null> {
  const body = await request<unknown>('GET', resourcePath(id));
  return parseResource(body);
}

/**
 * `POST /v2/admin/resources` → create a resource. Returns the re-parsed created row.
 * Throws a typed `ApiError` (`400` VALIDATION inline-able, `409` CONFLICT on the
 * unique `(tenant, name)` rule, upstream).
 */
export async function createResource(input: ResourceInput): Promise<V2Resource | null> {
  const body = await request<unknown>('POST', PATH, { json: toResourceBody(input) });
  return parseResource(body);
}

/**
 * `PUT /v2/admin/resources/{id}` → update a resource. Returns the re-parsed updated
 * row. Throws a typed `ApiError`.
 */
export async function updateResource(id: string, input: ResourceInput): Promise<V2Resource | null> {
  const body = await request<unknown>('PUT', resourcePath(id), { json: toResourceBody(input) });
  return parseResource(body);
}

/**
 * `POST /v2/admin/resources/{id}/deactivate` → soft-deactivate a resource (§4.3 —
 * never a hard delete). Throws a typed `ApiError`.
 */
export async function deactivateResource(id: string): Promise<void> {
  await request<unknown>('POST', `${resourcePath(id)}/deactivate`);
}

/**
 * `POST /v2/admin/resources/{id}/reactivate` → reactivate a soft-deactivated resource
 * (§4.3). Throws a typed `ApiError`.
 */
export async function reactivateResource(id: string): Promise<void> {
  await request<unknown>('POST', `${resourcePath(id)}/reactivate`);
}

// --- Location assignments (DNKADM-0054 U1) -------------------------------------

/**
 * `GET /v2/admin/resources/{id}/location-assignments` → the owned `v2_location.id`s
 * the resource is assigned to (the locations it can be scheduled at). Tolerantly
 * extracts the id array (bare array, or `{ locationIds }`/`{ locationAssignments }` of
 * ids or `{ locationId }` objects). Throws a typed `ApiError`.
 */
export async function getResourceLocations(id: string): Promise<string[]> {
  const body = await request<unknown>('GET', `${resourcePath(id)}/location-assignments`);
  let raw: unknown[] = [];
  if (Array.isArray(body)) raw = body;
  else if (isObject(body)) {
    if (Array.isArray(body['locationIds'])) raw = body['locationIds'];
    else if (Array.isArray(body['locationAssignments'])) raw = body['locationAssignments'];
    else if (Array.isArray(body['content'])) raw = body['content'];
  }
  const ids: string[] = [];
  for (const entry of raw) {
    // Accept a bare id (number or string) or an object carrying `locationId`/`id`.
    const id2 =
      asId(entry) ??
      (isObject(entry) ? asId(entry['locationId']) ?? asId(entry['id']) : null);
    if (id2 !== null && !ids.includes(id2)) ids.push(id2);
  }
  return ids;
}

/**
 * `PUT /v2/admin/resources/{id}/location-assignments` ← `{ locationIds }` → set the
 * WHOLE set of locations a resource is assigned to (the multi-select round-trips the
 * full set). Throws a typed `ApiError`.
 */
export async function setResourceLocations(id: string, locationIds: string[]): Promise<void> {
  await request<unknown>('PUT', `${resourcePath(id)}/location-assignments`, {
    json: { locationIds },
  });
}

// --- Weekly schedule per location (DNKADM-0054 U2) -----------------------------

/**
 * `GET /v2/admin/resources/{id}/schedule?locationId=…` → the resource's weekly
 * schedule AT that location (§5.7 — schedules are per-(resource, location)). Always
 * seven days in `dayOfWeek` 0→6 (Sun→Sat) order; a missing day fills unavailable.
 * Throws a typed `ApiError`.
 */
export async function getResourceSchedule(
  id: string,
  locationId: string,
): Promise<ResourceSchedule> {
  const body = await request<unknown>('GET', `${resourcePath(id)}/schedule`, {
    query: { locationId },
  });
  return parseSchedule(body);
}

/**
 * `PUT /v2/admin/resources/{id}/schedule?locationId=…` ← `{ days }` → set the WHOLE
 * 7-day multi-shift schedule for the resource at that location. Client validation
 * (`@/utils/shiftValidation`) should run first. Throws a typed `ApiError` (`400`
 * VALIDATION on a bad shift/overlap, upstream forwarded). Refetch after it resolves.
 */
export async function putResourceSchedule(
  id: string,
  locationId: string,
  days: ResourceScheduleDay[],
): Promise<void> {
  await request<unknown>('PUT', `${resourcePath(id)}/schedule`, {
    query: { locationId },
    json: { days: days.map(scheduleDayToBody) },
  });
}

// --- Date exceptions (DNKADM-0054 U2) ------------------------------------------

/**
 * `GET /v2/admin/resources/{id}/date-exceptions` → the resource's date exceptions
 * (defensively parsed; a bad entry dropped). Throws a typed `ApiError`.
 */
export async function listResourceDateExceptions(id: string): Promise<ResourceDateException[]> {
  const body = await request<unknown>('GET', `${resourcePath(id)}/date-exceptions`);
  return extractList(body, 'exceptions')
    .map(parseException)
    .filter((e): e is ResourceDateException => e !== null);
}

/**
 * `POST /v2/admin/resources/{id}/date-exceptions` ← one exception → the created row
 * (re-parsed). Client validation (`@/utils/shiftValidation`) should run on its shifts
 * first. Throws a typed `ApiError`. Refetch after it resolves.
 */
export async function createResourceDateException(
  id: string,
  input: ResourceDateExceptionInput,
): Promise<ResourceDateException | null> {
  const body = await request<unknown>('POST', `${resourcePath(id)}/date-exceptions`, {
    json: exceptionToBody(input),
  });
  return parseException(body);
}

/**
 * `PUT /v2/admin/resources/{id}/date-exceptions/{exceptionId}` ← the same body as the
 * create → the updated exception (DNKADM-0388 U3: a second block window on a date that
 * already has an OPEN exception updates it in place). Throws a typed `ApiError`.
 */
export async function updateResourceDateException(
  id: string,
  exceptionId: string,
  input: ResourceDateExceptionInput,
): Promise<ResourceDateException | null> {
  const body = await request<unknown>(
    'PUT',
    `${resourcePath(id)}/date-exceptions/${encodeURIComponent(exceptionId)}`,
    { json: exceptionToBody(input) },
  );
  return parseException(body);
}

/**
 * `DELETE /v2/admin/resources/{id}/date-exceptions/{exceptionId}` → remove one date
 * exception. Throws a typed `ApiError`. Refetch after it resolves.
 */
export async function deleteResourceDateException(
  id: string,
  exceptionId: string,
): Promise<void> {
  await request<unknown>(
    'DELETE',
    `${resourcePath(id)}/date-exceptions/${encodeURIComponent(exceptionId)}`,
  );
}

// --- The shared, fail-closed resource pick-list (DNKADM-0054 U1) ----------------

/**
 * Load the ACTIVE resources as `{ id, name }` options, **fail-closed**: any
 * upstream/network failure OR a malformed (non-array) `200` body throws the shared
 * {@link PickListError}, so the loader renders error + Retry and NEVER a silent empty
 * list (a failure can never masquerade as "loaded, empty"). A successful empty list
 * returns `[]` (a real first-run state). `name` is the resource name, falling back to
 * the id. Only `active` resources are offered (a deactivated resource must not be
 * selectable on a service, 0055).
 *
 * Mirrors `getStaffOptions` / `getServiceCategoryOptions`, reusing their
 * `PickListError` so `usePickList`'s `failClosed` detection (and the loader copy) work
 * unchanged. `ServiceDetail` passes this fetcher through `usePickList` for the service
 * form (0055).
 *
 * `includeInactive` (opt-in; default `false`) broadens the fetch to `status=all` and
 * carries each resource's `isActive` through — used only by the service-detail
 * eligible-resources editor (DNKADM-0134), so an already-assigned but since-deactivated
 * resource stays visible/editable. The no-args default preserves today's active-only
 * behavior.
 */
export async function getResourceOptions(
  options: { includeInactive?: boolean } = {},
): Promise<ResourceOption[]> {
  try {
    const status = options.includeInactive ? 'all' : 'active';
    const body = await request<unknown>('GET', PATH, { query: { status } });
    const list = extractResources(body);
    // A non-array success cannot be trusted as a COMPLETE list — fail-closed.
    if (list === null) throw new Error('expected an array of resources');
    return list
      .map(parseResource)
      .filter((r): r is V2Resource => r !== null)
      .map((r) => ({ id: r.id, name: r.name.length > 0 ? r.name : r.id, isActive: r.isActive }));
  } catch (cause) {
    throw new PickListError('resources', cause);
  }
}
